<script setup lang="ts">
import type { ButtonProps } from "./button.types";
import { useButton } from "./useButton";

defineOptions({ name: "EButton", inheritAttrs: false });

const props = withDefaults(defineProps<ButtonProps>(), {
  color: "neutral",
  variant: "subtle",
  size: "md",
  disabled: false,
  loading: false,
  square: false,
  block: false,
});

const {
  state,
  classes,
  showLeading,
  showLabel,
  showTrailing,
  leadingIconName,
  trailingIconName,
  iconSize,
  isLoading,
  isDisabled,
  resolvedTag,
  href,
  target,
  buttonType,
  ariaDisabled,
  ariaBusy,
  innerRef,
} = useButton({ props });
</script>

<template>
  <e-primitive
    :ref="(el: any) => (innerRef = el)"
    :as="resolvedTag"
    exportparts="base"
    :inner-class="classes.base"
    class="e-button"
    :href="href"
    :target="target"
    :type="buttonType"
    :disabled="isDisabled || isLoading || undefined"
    :aria-disabled="ariaDisabled"
    :aria-busy="ariaBusy"
    v-bind="$attrs"
  >
    <span v-show="showLeading" part="leading" :class="classes.icon" class="e-button-leading">
      <slot name="leading">
        <e-icon
          v-if="leadingIconName"
          exportparts="base:leading-icon"
          :name="leadingIconName"
          :class="{ 'animate-spin': isLoading && !props.trailing }"
          :size="iconSize"
        />
      </slot>
    </span>

    <span v-show="showLabel" part="label" :class="classes.label" class="e-button-label">
      <slot>{{ state.label?.value ?? props.label }}</slot>
    </span>

    <span v-show="showTrailing" part="trailing" :class="classes.icon" class="e-button-trailing">
      <slot name="trailing">
        <e-icon
          v-if="trailingIconName"
          exportparts="base:trailing-icon"
          :name="trailingIconName"
          :class="{ 'animate-spin': isLoading && props.trailing }"
          :size="iconSize"
        />
      </slot>
    </span>
  </e-primitive>
</template>

<style>
:host {
  display: contents;
}
</style>
